/**
 * Presentation for a COMPOSED game (a GameSpec from
 * `@djgg/gameplay-kit/compose`), in the same shape the maintained
 * profiles' composition presentation uses — summary, projection, background
 * role, world, palette and render kinds with roles and coded renders — plus
 * each kind's animation STATES, which the kit derives from the modules
 * attached to it. Media slots are therefore derived, never listed:
 * one sprite per kind, one strip `<kind>-<state>` per kind × state, one
 * background. The structural (coded) render manifest carries no `frames`:
 * it is the coded prototype every build proves first. Binding generated
 * strips is what adds `frames` (state → `<kind>-<state>`) and `strips`, so a
 * bound strip animates and an unbound state holds the static sprite. The
 * spec's camera model (`fixed | follow | auto-scroll`) maps onto the manifest
 * here, once: a follow camera presents its VIEW as the manifest world and its
 * simulated world as the camera's level (the platform follows the player);
 * fixed and auto-scroll views ARE the screen and pin no manifest camera (an
 * auto-scroll ground is scrolled by the composed adapter's backdrop).
 */
import { derivePresentationIn, type ComposeLibraryV1, type ComposedPresentationV1 } from '@djgg/gameplay-kit/compose';
import { stripAssetName, type AnimStateName } from '../animation.js';
import type { CompositionFacingV1 } from './compositionPresentation.js';
import { RENDER_MANIFEST_VERSION, validateRenderManifest, type CameraSpec, type EntityRender, type RenderManifest } from '../renderManifest.js';

export const COMPOSED_BACKGROUND_SLOT = 'background';

export interface ComposedCompositionEntityV1 {
  role: string;
  /** Present only for a kind recoloured per instance (art generated neutral). */
  art?: 'neutral';
  render: EntityRender;
  /** Animation states this kind can show — its strip slots. */
  states: AnimStateName[];
  /** Declared art facing and sign label (the composition presentation's). */
  facing?: CompositionFacingV1;
  label?: string;
  /** The kind whose accepted art this kind's is recoloured from (the spec's
   * `render.derivedFrom`): never generated, never judged. */
  derivedFrom?: string;
}

export interface ComposedCompositionPresentationV1 {
  summary: string;
  projection: ComposedPresentationV1['projection'];
  backgroundRole: string;
  /** The screen's world: the whole simulated world, or a follow camera's view. */
  world: RenderManifest['world'];
  camera?: CameraSpec;
  palette: string[];
  backgroundPaletteIndex: number;
  entities: Record<string, ComposedCompositionEntityV1>;
  /** A body in this game stands on, walks along, climbs or lands on a surface
   * the game draws itself (derived from its modules; `sceneryOnly.ts`). */
  surfaceContact: boolean;
}

/** Validates the spec against its library (throws GameSpecError) and derives its presentation. */
export function composedCompositionPresentationV1(spec: unknown, library: ComposeLibraryV1): ComposedCompositionPresentationV1 {
  const p = derivePresentationIn(library, spec);
  const camera = p.camera.mode === 'follow'
    ? { world: { ...p.camera.view }, camera: { level: { ...p.world }, lookAhead: p.camera.lookAhead, ease: p.camera.ease, bias: { ...p.camera.bias } } }
    : { world: { ...p.world } };
  return {
    summary: p.summary, projection: p.projection, backgroundRole: p.backgroundRole, ...camera,
    palette: [...p.palette], backgroundPaletteIndex: p.backgroundPaletteIndex, surfaceContact: p.surfaceContact,
    entities: Object.fromEntries(Object.entries(p.entities).map(([kind, e]) => {
      const render: EntityRender = {
        sprite: 'builtin:rectangle', palette: e.render.palette, size: { ...e.size }, depth: e.render.depth,
        ...(e.render.anchor === 'bottom' && !e.render.surface ? { anchor: 'bottom' as const } : {}),
        ...(e.render.surface ? { surface: true } : {}),
        // A surface whose art carries its sign is one object: drawn once.
        ...(e.render.surface && e.render.label !== undefined ? { surfaceArt: 'once' as const } : {}),
        ...(e.render.shadow ? { shadow: true } : {}),
      };
      return [kind, { role: e.role, ...(e.render.neutral ? { art: 'neutral' as const } : {}), render, states: [...e.states],
        ...(e.render.facing ? { facing: e.render.facing } : {}), ...(e.render.label !== undefined ? { label: e.render.label } : {}),
        ...(e.render.derivedFrom !== undefined ? { derivedFrom: e.render.derivedFrom } : {}) }];
    })),
  };
}

/** The coded render manifest a composed build ships before art binds. */
export function composedRenderManifestV1(spec: unknown, library: ComposeLibraryV1): RenderManifest {
  const p = composedCompositionPresentationV1(spec, library);
  return validateRenderManifest({
    version: RENDER_MANIFEST_VERSION, world: p.world, ...(p.camera ? { camera: p.camera } : {}), palette: p.palette,
    entities: Object.fromEntries(Object.entries(p.entities).map(([kind, e]) => [kind, e.render])),
    background: { paletteIndex: p.backgroundPaletteIndex },
  });
}

/** Derived media slots: sprite per kind + background (sorted), and the strip
 * names `<kind>-<state>` (sorted) that animate them. */
export function composedMediaSlotsV1(spec: unknown, library: ComposeLibraryV1): { sprites: string[]; strips: string[] } {
  const p = composedCompositionPresentationV1(spec, library);
  return {
    sprites: [...Object.keys(p.entities), COMPOSED_BACKGROUND_SLOT].sort(),
    strips: Object.entries(p.entities).flatMap(([kind, e]) => e.states.map(state => stripAssetName(kind, state))).sort(),
  };
}
